import { useState, useEffect } from "react";
import {
  acceptUser,
  redirectSignIn,
  UseUserAuth,
} from "./context/UseAuthContext";
import PageNav from "./components/PageNav";
import PasswordLenAlert from "./PasswordLenAlertBox";
import { useNavigate, NavLink } from "react-router-dom";
import WrEmailAlert from "./WrongEmailAlertBox";
import app from "./context/Firebase";
import { getAnalytics, logEvent } from "firebase/analytics";
import InvalidCodeAlert from "./InvalidCodeAlert";

function EducatorSignup() {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const { signUp } = UseUserAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [alertPasswordBox, setAlertPasswordBox] = useState(false);
  const [wrongEmailAlert, setWrongEmailAlert] = useState(false);
  const [invalidCodeAlert, setInvalidCodeAlert] = useState(false);
  const navigate = useNavigate();

  // Organization-related state
  const [accountType, setAccountType] = useState("individual");
  const [organizationOption, setOrganizationOption] = useState("create");
  const [organizationName, setOrganizationName] = useState("");
  const [joinCode, setJoinCode] = useState("");

    // This function handles retrying with a new join code
    const handleRetryJoinCode = () => {
      setInvalidCodeAlert(false);
      setJoinCode(""); // Clear the join code for re-entry
    };

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    
    // Validation
    if (password.length < 6) {
      setAlertPasswordBox(true);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setWrongEmailAlert(true);
      return;
    }

    // Additional validation for organization information
    if (accountType === "individual") {
      if (!organizationName.trim()) {
        setError("School/Childcare center name is required for individual educators");
        return;
      }
    } else if (accountType === "organization") {
      if (organizationOption === "create" && !organizationName.trim()) {
        setError("Organization name is required");
        return;
      }
      if (organizationOption === "join" && !joinCode.trim()) {
        setError("Join code is required");
        return;
      }
   /*   // PRE-VALIDATE JOIN CODE before creating Firebase account
      if (organizationOption === "join") {
        const isValidCode = await validateJoinCode(joinCode);
        if (!isValidCode) {
          setInvalidCodeAlert(true);
          return;
        }
      }*/
    }

    try {
      const name = firstName + " " + lastName;
      
      // Create user with Firebase Auth
      const userCredential = await signUp(email, password, name);
      console.log("Signup - userCredential:", userCredential);
      
      // Prepare B2B info with correct role assignment
      const provider = "Email";
      const b2bInfo = {
        accountType: accountType,
        userType: "educator",
        // Set userRole to admin only for individual educators or org creators, not joiners
        userRole: (accountType === "individual" || 
                  (accountType === "organization" && organizationOption === "create")) 
                  ? "admin" : "member",
        organizationInfo: accountType === "organization" 
          ? {
              organizationOption: organizationOption,
              organizationName: organizationOption === "create" ? organizationName : null,
              joinCode: organizationOption === "join" ? joinCode : null
            } 
          : { organizationName: organizationName, isIndividual: true }
      };
    try{  
      // Pass B2B info to acceptUser function
      await acceptUser(userCredential, navigate, provider, name, b2bInfo);
      
      const analytics = getAnalytics(app);
      logEvent(analytics, "sign_up", {
        method: provider,
        accountType: accountType,
        userType: "educator"
      });
    } catch (acceptError) {
      console.error("Accept user error:", acceptError);
      // Check if the error is related to an invalid join code
        if (acceptError.message && 
           (acceptError.message.includes("Invalid join code") || 
            (acceptError.status && acceptError.status === 400))) {
          // Show the invalid code alert
          setInvalidCodeAlert(true);
       
       // If user was created in Firebase Auth but join code was invalid, 
       // we might need to handle this situation (optional)
       // This could involve deleting the created user or marking it for cleanup
     } else {
       // For other errors
       setError(acceptError.message || "Error during signup process");
     }
    }}catch (error) {
   console.error("Signup error:", error);
   setError(error.message);
 }
}

  useEffect(() => {
    redirectSignIn(navigate);
  }, [navigate]);

  return (
    <>
      <PageNav />
      <div className="flex min-h-full flex-1 flex-col justify-center py-8 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            className="mx-auto h-14 w-auto"
            src="https://static.mytalemaker.com/logo-icon.png"
            alt="MyTaleMaker"
          />
          <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
            Sign up for Educators
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create stories for your classroom or educational organization
          </p>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-[480px]">
          <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  First name
                </label>
                <div className="mt-2">
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{ padding: "10px" }}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Last name
                </label>
                <div className="mt-2">
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{ padding: "10px" }}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Email address
                </label>
                <div className="mt-2">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{ padding: "10px" }}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password
                </label>
                <div className="mt-2 relative">
                  <input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    style={{ padding: "10px" }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              {/* Account Type Selection */}
              <div className="pt-4">
                <label className="block text-sm font-medium leading-6 text-gray-900 mb-2">
                  I am signing up as:
                </label>
                <div className="space-y-3">
                  <div className="relative flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex items-center h-5">
                      <input
                        id="individual"
                        name="accountType"
                        type="radio"
                        value="individual"
                        checked={accountType === "individual"}
                        onChange={() => setAccountType("individual")}
                        className="h-4 w-4 text-indigo-600 border-gray-300"
                      />
                    </div>
                    <div className="ml-3 flex-grow">
                      <label htmlFor="individual" className="font-medium text-gray-900">Individual Educator</label>
                      <p className="text-sm text-gray-500">I'm the only user from my school, childcare center, or classroom.</p>
                    </div>
                  </div>
                  
                  <div className="relative flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <div className="flex items-center h-5">
                      <input
                        id="organization"
                        name="accountType"
                        type="radio"
                        value="organization"
                        checked={accountType === "organization"}
                        onChange={() => setAccountType("organization")}
                        className="h-4 w-4 text-indigo-600 border-gray-300"
                      />
                    </div>
                    <div className="ml-3 flex-grow">
                      <label htmlFor="organization" className="font-medium text-gray-900">Educational Organization</label>
                      <p className="text-sm text-gray-500">I'm part of a school, district, or educational organization with multiple users.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Organization Name (for individual educators) */}
              {accountType === "individual" && (
                <div className="pt-2 pb-2">
                  <label
                    htmlFor="individualOrgName"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    School/Childcare Center Name
                  </label>
                  <div className="mt-2">
                    <input
                      id="individualOrgName"
                      name="individualOrgName"
                      type="text"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      placeholder="e.g., Sunshine Daycare, Lincoln Elementary"
                      required
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      style={{ padding: "10px" }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Please provide the name of your school, childcare center, or educational institution.
                  </p>
                </div>
              )}

              {/* Organization Details (only shown if organization is selected) */}
              {accountType === "organization" && (
                <div className="pt-2 pb-2 space-y-4">
                  <div className="flex flex-col space-y-1">
                    <label className="block text-sm font-medium leading-6 text-gray-900">
                      Organization Options:
                    </label>
                    <div className="flex space-x-4 mt-1 mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="organizationOption"
                          value="create"
                          checked={organizationOption === "create"}
                          onChange={() => setOrganizationOption("create")}
                          className="mr-2"
                        />
                        <span className="text-sm">Create a new organization</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="organizationOption"
                          value="join"
                          checked={organizationOption === "join"}
                          onChange={() => setOrganizationOption("join")}
                          className="mr-2"
                        />
                        <span className="text-sm">Join existing organization</span>
                      </label>
                    </div>
                  </div>

                  {organizationOption === "create" ? (
                    <div>
                      <label
                        htmlFor="organizationName"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Organization Name
                      </label>
                      <div className="mt-2">
                        <input
                          id="organizationName"
                          name="organizationName"
                          type="text"
                          value={organizationName}
                          onChange={(e) => setOrganizationName(e.target.value)}
                          required={organizationOption === "create"}
                          placeholder="School or district name"
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          style={{ padding: "10px" }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label
                        htmlFor="joinCode"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Organization Join Code
                      </label>
                      <div className="mt-2">
                        <input
                          id="joinCode"
                          name="joinCode"
                          type="text"
                          value={joinCode}
                          onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                          required={organizationOption === "join"}
                          placeholder="Enter 6-character code"
                          maxLength={6}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          style={{ padding: "10px" }}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        The join code should be provided to you by your organization administrator.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="text-red-500 text-sm mt-2">
                  {error}
                </div>
              )}

              <p className="text-center text-xs text-gray-500 mt-2 mb-4">
                By registering for and using our service, you consent to our
                <NavLink
                  to="/policy"
                  className="text-indigo-600 hover:text-indigo-800"
                >
                  {" "}
                  Terms of Use And Privacy Policy
                </NavLink>
              </p>
              
              <div className="flex justify-center">
                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  style={{ width: "189px", height: "40px" }}
                >
                  Sign up
                </button>
              </div>
            </form>
            
            <div className="text-center mt-4">
              <p className="text-gray-700">
                Already have an account?{" "}
                <NavLink
                  to="/login"
                  className="text-sm text-indigo-500 hover:text-indigo-700 font-semibold"
                >
                  Log In
                </NavLink>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {alertPasswordBox && (
        <PasswordLenAlert
          onConfirm={() => setAlertPasswordBox(false)}
        />
      )}
      {wrongEmailAlert && (
        <WrEmailAlert
          onConfirm={() => setWrongEmailAlert(false)}
        />
      )}
      {invalidCodeAlert && (
        <InvalidCodeAlert
          onConfirm={() => setInvalidCodeAlert(false)}
          onRetry={handleRetryJoinCode}
        />
      )}
    </>
  );
}

export default EducatorSignup;